import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, InputAdornment, TablePagination, TableSortLabel, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CurrencyExchangeRoundedIcon from "@mui/icons-material/CurrencyExchangeRounded";
import moment from "moment-timezone";

import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import { tokens } from "../../theme";
import { mono, pill, th, td, TablePaginationActions } from "../../components/tableKit";

const fmtDateTime = (d) =>
  d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm:ss") : "";

// Webhook events the payment API logs into the error log: refund lifecycle
// (feature/refund) and deposit failures.
const isPaymentEvent = (r) => {
  const type = String(r.errorType || "").toLowerCase();
  const code = String(r.errorCode || "").toUpperCase();
  return (
    type === "refund" ||
    /^(REFUND|CHARGEBACK|SETTLEMENT)/.test(code) ||
    (type === "deposit" && /^PAYMENT(_HANDLE)?_(FAILED|ERRORED|EXPIRED)/.test(code))
  );
};

const eventKind = (code) => {
  const c = String(code || "").toUpperCase();
  if (/COMPLETED/.test(c)) return "ok";
  if (/RECEIVED|PENDING|PROCESSING|INITIATED/.test(c)) return "warn";
  if (/FAILED|CANCELLED|ERRORED|EXPIRED|CHARGEBACK/.test(c)) return "bad";
  return "neutral";
};

// Refund rows carry "refundId=... | merchantRefNum=... | amount=..." in the message.
const fromDetail = (message, key) => {
  const m = String(message || "").match(new RegExp(`${key}=([^ |)]+)`));
  return m && m[1] !== "null" ? m[1] : "";
};

// Paysafe webhook amounts are minor units (cents).
const fmtAmount = (raw) => {
  const n = Number(raw);
  return raw !== "" && !isNaN(n) ? `$${(n / 100).toFixed(2)}` : "—";
};

export default function PaymentEvents() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedKind, setSelectedKind] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdOn", direction: "desc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const response = await getAllData(endPoints.api.GET_ERROR_LOG);
      if (response?.status === "success") {
        setRows((response.data || []).filter(isPaymentEvent));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const kindOf = (r) => {
    const code = String(r.errorCode || "").toUpperCase();
    if (/^CHARGEBACK/.test(code)) return "Chargeback";
    if (String(r.errorType || "").toLowerCase() === "refund" || /^REFUND/.test(code)) return "Refund";
    if (/^SETTLEMENT/.test(code)) return "Rollback";
    return "Payment failure";
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
    setPage(0);
  };

  const rowsToDisplay = useMemo(() => {
    let result = rows;

    if (selectedKind) {
      result = result.filter((r) => kindOf(r) === selectedKind);
    }

    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (r) =>
          String(r.userId || "").includes(q) ||
          String(r.errorCode || "").toLowerCase().includes(q) ||
          String(r.errorMessage || "").toLowerCase().includes(q)
      );
    }

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let av = a[sortConfig.key] ?? "";
        let bv = b[sortConfig.key] ?? "";
        let cmp;
        if (!isNaN(Number(av)) && !isNaN(Number(bv)) && av !== "" && bv !== "") {
          cmp = Number(av) - Number(bv);
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
        }
        return sortConfig.direction === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [rows, selectedKind, searchText, sortConfig]);

  const sortableTh = (key, label) => (
    <th style={th}>
      <TableSortLabel
        active={sortConfig.key === key}
        direction={sortConfig.key === key ? sortConfig.direction : "desc"}
        onClick={() => handleSort(key)}
        sx={{ "&.MuiTableSortLabel-root": { color: "inherit" }, "& .MuiTableSortLabel-icon": { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    </th>
  );

  const refunds = rows.filter((r) => kindOf(r) === "Refund");
  const refundsCompleted = refunds.filter((r) => /COMPLETED/i.test(r.errorCode || "")).length;
  const last24h = rows.filter(
    (r) => r.createdOn && moment.utc(r.createdOn).isAfter(moment.utc().subtract(24, "hours"))
  ).length;

  return (
    <Box>
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Payment Events"
        crumb="Monitor / Payment Events"
        subtitle="Refunds and payment failures reported by the Paysafe webhook"
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75, mb: 1.75 }}>
        <Box sx={{ flex: "1 1 200px" }}>
          <MiniStat label="Refunds" value={String(refunds.length)} note={`${refundsCompleted} completed`} color={tokens.amber} />
        </Box>
        {/* Chargebacks tile - removed for now: the webhook only handles refund events
            (SkrillWebhookController.handleRefundEvent), so this would always read 0.
            Re-add once chargeback events are wired into the webhook. */}
        <Box sx={{ flex: "1 1 200px" }}>
          <MiniStat label="Last 24 hours" value={String(last24h)} note="all payment events" color={tokens.primary} />
        </Box>
      </Box>

      <SectionCard>
        {/* Kind filter + search - commented out until there's real data worth
            filtering/searching. Re-enable by uncommenting this Box.
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            select
            size="small"
            label="Kind"
            value={selectedKind}
            onChange={(e) => {
              setSelectedKind(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180, "& .MuiInputBase-root": { fontSize: 13 } }}
          >
            <MenuItem value="">All kinds</MenuItem>
            {["Refund", "Chargeback", "Rollback", "Payment failure"].map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            placeholder="Search by user ID, event or message"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 320, "& .MuiInputBase-root": { fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        */}

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {sortableTh("errorCode", "Event")}
                <th style={th}>Kind</th>
                {sortableTh("userId", "User")}
                <th style={th}>Amount</th>
                <th style={th}>Merchant Ref</th>
                <th style={th}>Detail</th>
                {sortableTh("createdOn", "Logged At")}
              </tr>
            </thead>
            <tbody>
              {rowsToDisplay.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={7} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No payment events yet — refunds and failures will appear here as the webhook reports them
                  </td>
                </tr>
              )}
              {rowsToDisplay
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <tr key={row.id}>
                    <td style={td}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <CurrencyExchangeRoundedIcon sx={{ fontSize: 15, color: tokens.textDim }} />
                        {pill(row.errorCode || "—", eventKind(row.errorCode))}
                      </Box>
                    </td>
                    <td style={td}>{kindOf(row)}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.userId ? `#${row.userId}` : "—"}</td>
                    <td style={{ ...td, fontFamily: mono }}>{fmtAmount(fromDetail(row.errorMessage, "amount"))}</td>
                    <td style={{ ...td, fontFamily: mono }}>{fromDetail(row.errorMessage, "merchantRefNum") || "—"}</td>
                    <td style={{ ...td, whiteSpace: "normal", maxWidth: 480, color: tokens.textDim }}>
                      {row.errorMessage}
                    </td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDateTime(row.createdOn)}</td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={rowsToDisplay.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>
    </Box>
  );
}
